'use client';

import { useEffect, useRef, useState } from 'react';

const DURATION_MS = 1800;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/**
 * Count-up that renders the REAL `target` value in the markup so SSR / no-JS /
 * crawlers / pre-hydration (Safari) never see "0" (audit M-05). The 0 → target
 * animation is a pure progressive enhancement that plays once JS is running:
 * elements above the fold count up on load, and below-the-fold ones are primed
 * to 0 off-screen and count up when scrolled into view. Reduced-motion users
 * and a `target` of 0 keep the static real value.
 *
 * Returns the value to render and a ref to attach to the element being watched.
 */
export function useCountUp<T extends HTMLElement = HTMLDivElement>(
  target: number,
) {
  const [display, setDisplay] = useState(target);
  const ref = useRef<T>(null);
  const hasRun = useRef(false);
  const isFirstObservation = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') {
      return;
    }

    // Respect reduced motion and the 0 case: keep the static real value.
    // `display` is already initialized to `target`, so nothing to set here.
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      target === 0
    ) {
      return;
    }

    const isIntegerTarget =
      Number.isInteger(target) || Math.abs(target - Math.round(target)) < 1e-9;

    const animate = () => {
      if (hasRun.current) {
        return;
      }
      hasRun.current = true;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / DURATION_MS);
        const next = easeOutCubic(t) * target;
        setDisplay(
          isIntegerTarget ? Math.round(next) : Math.round(next * 100) / 100,
        );
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          setDisplay(target);
        }
      };
      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isFirstObservation.current) {
          isFirstObservation.current = false;
          // The real value is already in the SSR markup (M-05), so no-JS /
          // crawlers / pre-hydration never see 0. Now that JS is confirmed
          // running, reset to 0 and count up — above the fold animates right
          // away, below the fold waits for the scroll-in (next observation).
          setDisplay(0);
          if (entry.isIntersecting) {
            animate();
          }
          return;
        }
        if (entry.isIntersecting) {
          animate();
        }
      },
      { threshold: 0.25, rootMargin: '0px 0px -10% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return { display, ref };
}
